import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { Projects } from './components/Projects';
import { Experience } from './components/Experience';
import { LanguageToggle } from './components/LanguageToggle';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { Blog } from './pages/Blog';
import { Testimonials } from './pages/Testimonials';
import { Contact } from './pages/Contact';
import { Terms } from './pages/Terms';
import { Cookies } from './pages/Cookies';
import { Privacy } from './pages/Privacy';
import { About } from './pages/About';
import { Services } from './pages/Services';
import { ServicesTeaser } from './components/ServicesTeaser';
import { Demo } from './pages/Demo';
import AIChat from './components/AIChat';
import { CookieConsent } from './components/CookieConsent';
import { AnalyticsScripts } from './components/AnalyticsScripts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SEO from './components/SEO';
import { Toaster } from 'react-hot-toast';
import { trackPageView } from './lib/analytics';
import { ThemeProvider } from './context/ThemeContext';
import './i18n/config';

const queryClient = new QueryClient();

// Route change tracking component
function RouteChangeTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
    // Start each page from the top (unless navigating to an in-page anchor)
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);
  
  return null;
}

function HomePage() {
  return (
    <>
      <SEO
        title="Victor Lazari – Consulente Power Platform, Dati e Automazione per PMI"
        description="Consulente indipendente per PMI e system integrator: app gestionali Power Apps, dashboard Power BI, automazione processi e integrazioni dati. Ex Baker Hughes, 200+ processi aziendali supportati. Ravenna e remoto in tutta Italia."
        canonical="https://victorlazari.com/"
        image="https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/Victor%20Lazari%20IT%20Consultant.png"
        type="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Victor Lazari',
          url: 'https://victorlazari.com/',
          jobTitle: 'Power Platform & Automation Consultant',
          knowsAbout: ['Power Apps', 'Power Automate', 'Power BI', 'SQL Server', 'Azure'],
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Ravenna',
            addressCountry: 'IT'
          }
        }}
      />
      <Hero />
      <ServicesTeaser />
      <Projects />
      <Experience />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <div className="relative min-h-screen bg-white dark:bg-gray-900 text-slate-900 dark:text-gray-100 overflow-hidden flex flex-col transition-colors duration-200">
            <div className="relative z-10 flex-1">
              <RouteChangeTracker />
              <Navigation />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<Blog />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/solutions" element={<Services />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/about" element={<About />} />
              </Routes>
            </div>
            <div className="relative z-10">
              <Footer />
            </div>
            <AIChat />
            <CookieConsent />
            <AnalyticsScripts />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--toast-bg, #ffffff)',
                  color: 'var(--toast-color, #111827)',
                  border: 'var(--toast-border, 1px solid #e5e7eb)',
                },
                className: 'dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700',
              }}
            />
          </div>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;